import { apiClient } from './axios';

// Types
export interface ReferralStats {
  total: number;
  pending: number;
  completed: number;
  totalPointsEarned: number;
}

export interface ReferredUser {
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  userType: string;
}

export interface Referral {
  id: string;
  referredUser: ReferredUser | null;
  status: 'pending' | 'completed';
  referredUserType: 'owner' | 'professional';
  pointsAwarded: number;
  createdAt: string;
  completedAt: string | null;
}

export interface GetReferralsResponse {
  referralCode: string;
  stats: ReferralStats;
  referrals: Referral[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ValidateReferralCodeResponse {
  valid: boolean;
  referrer?: {
    firstName?: string;
    imageUrl?: string;
  };
  error?: string;
}

export interface ApplyReferralCodeResponse {
  success: boolean;
  message: string;
  referrer?: {
    firstName?: string;
  };
  error?: string;
}

export interface GetMyReferralCodeResponse {
  referralCode: string;
}

/** Referral program config from backend (synced with Admin). */
export interface ReferralConfigResponse {
  ownerCompletedPoints: number;
  professionalCompletedPoints: number;
  capPerReferrer: number | null;
  webappLink: { baseUrl: string; pathTemplate: string };
  petsoProLink: { baseUrl: string; pathTemplate: string };
}

// API Functions

/**
 * Get referral program config from backend (points, cap, links). Used so webapp shows same values as Admin.
 */
export async function getReferralConfigFromApi(): Promise<ReferralConfigResponse | null> {
  try {
    const response = await apiClient.get<ReferralConfigResponse>('/referrals/config');
    return response.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Get current user's referral stats and list of referrals
 */
export async function getReferrals(
  status?: 'pending' | 'completed' | 'all',
  limit?: number,
  offset?: number
): Promise<GetReferralsResponse> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (limit !== undefined) params.limit = limit.toString();
  if (offset !== undefined) params.offset = offset.toString();

  const response = await apiClient.get<GetReferralsResponse>('/referrals', { params });
  return response.data;
}

/**
 * Validate a referral code (public endpoint)
 */
export async function validateReferralCode(code: string): Promise<ValidateReferralCodeResponse> {
  const response = await apiClient.get<ValidateReferralCodeResponse>(`/referrals/code/${code}`);
  return response.data;
}

/**
 * Apply a referral code to current user
 */
export async function applyReferralCode(referralCode: string): Promise<ApplyReferralCodeResponse> {
  const response = await apiClient.post<ApplyReferralCodeResponse>('/referrals/apply', {
    referralCode,
  });
  return response.data;
}

/**
 * Get current user's referral code
 */
export async function getMyReferralCode(): Promise<GetMyReferralCodeResponse> {
  const response = await apiClient.get<GetMyReferralCodeResponse>('/referrals/my-code');
  return response.data;
}
